import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Book } from '@booknest/shared';

import type { UpcomingFromAuthorItem } from '@/features/library/api/library.api.js';
import { BookModal } from '@/features/library/components/BookModal.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { useUpcomingFromFavoriteAuthors } from '@/features/library/hooks/useUpcomingFromFavoriteAuthors.js';

const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric' });
const MONTH_FORMAT = new Intl.DateTimeFormat('fr-FR', { month: 'short' });

/**
 * Section home : prochaines sorties des auteurs présents dans la shelf du
 * user. Source distincte de `ReleaseList` (qui reste branchée sur la wishlist).
 */
export function AuthorReleasesList({ limit = 6 }: { limit?: number }): JSX.Element {
  const { data, isLoading } = useUpcomingFromFavoriteAuthors(limit);
  const { data: shelfData } = useMyShelf();
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);

  const selectedItem =
    data?.items.find((it) => it.book.id === selectedBookId) ?? null;
  const selectedEntry =
    shelfData?.items.find((e) => e.book.id === selectedBookId) ?? null;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold text-brown">
            Bientôt en librairie
          </h2>
          <p className="font-script text-lg text-brown-soft">
            de tes autrices préférées
          </p>
        </div>
        <Link
          to="/upcoming"
          className="text-sm font-semibold text-terracotta hover:underline"
        >
          Calendrier →
        </Link>
      </div>

      {isLoading && <p className="text-sm text-brown-soft">Chargement…</p>}

      {!isLoading && (!data || data.items.length === 0) && (
        <div className="rounded-3xl bg-paper p-6 shadow-md">
          <p className="text-sm text-brown-soft">
            Pas encore de sortie repérée. Ajoute des livres à ta bibliothèque
            pour que Booknest apprenne tes auteurs.
          </p>
          <Link
            to="/discover"
            className="mt-3 inline-block text-sm font-semibold text-terracotta hover:underline"
          >
            → Explore le catalogue
          </Link>
        </div>
      )}

      {data && data.items.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {data.items.map((it) => (
            <AuthorReleaseRow
              key={it.book.id}
              item={it}
              onClick={() => setSelectedBookId(it.book.id)}
            />
          ))}
        </ul>
      )}

      {selectedItem && (
        <BookModal
          book={selectedItem.book}
          entry={selectedEntry}
          onClose={() => setSelectedBookId(null)}
        />
      )}
    </section>
  );
}

function AuthorReleaseRow({
  item,
  onClick,
}: {
  item: UpcomingFromAuthorItem;
  onClick: () => void;
}): JSX.Element {
  const book: Book = item.book;
  const date = book.releaseDate ? new Date(book.releaseDate) : null;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-4 rounded-2xl bg-paper p-3 text-left shadow-sm transition-transform hover:translate-x-1"
        aria-label={`Ouvrir ${book.title}`}
      >
        <div className="flex h-[60px] w-14 flex-shrink-0 flex-col items-center justify-center rounded-xl bg-gradient-to-br from-sage-light to-sage text-cream-light shadow-sm shadow-sage/30">
          <span className="font-serif text-xl font-semibold leading-none">
            {date ? DAY_FORMAT.format(date) : '—'}
          </span>
          <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider">
            {date ? MONTH_FORMAT.format(date) : ''}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-serif text-base font-semibold text-brown">
            {book.title}
          </p>
          <p className="truncate text-xs text-brown-soft">
            {item.author}
            {book.genre && ` · ${book.genre}`}
          </p>
        </div>
        <span
          aria-hidden
          className="flex-shrink-0 rounded-full bg-terracotta/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-terracotta-dark"
        >
          À venir
        </span>
      </button>
    </li>
  );
}
